import React, {
  useCallback,
  useState,
  useContext,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { orderingPhysicianLabel } from '@mummssoftware/utils/medications/utils';
import { FormattedMessage } from 'react-intl';
import { SuggestBox } from '@mummssoftware/common-ui';
import { TableCell } from '@material-ui/core';
import { messages } from '../Medication/messages';
import { AppContext } from '../../App/AppContext';
import { MedContext } from '../../App/MedContext';

const StyledTableCell = styled(TableCell)`
  && {
    color: inherit;
    white-space: nowrap;
    ${(props) =>
      props.smallwidget
        ? `padding: 2px;
           font-size: 14px;`
        : `padding: 1px;
           font-size: 15px;`}
  }
`;

export const MedPhysicianPicker = (props) => {
  const { medication, handleChange, isFromMedOrder } = props;

  const {
    attendingAndHospicePhysicianList,
    allDoctors,
    readonly,
    isMobile,
    smallWidget,
  } = useContext(AppContext);
  const { isMobileEditable } = useContext(MedContext);
  const triggerRef = useRef(null); // Ref for positioning calculations
  const selectRef = useRef(null); // Ref to call openMenu() programmatically

  // attendingAndHospicePhysicianList.push({ value: 'more', label: 'more...' });
  const [maxHeight, setMaxHeight] = useState(0);
  const [menuPlacement, setMenuPlacement] = useState('bottom');

  useEffect(() => {
    if (!smallWidget) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll
      document.body.style.overflow = '';
    }

    // Cleanup when unmounting
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useLayoutEffect(() => {
    function updateMaxHeight() {
      if (!triggerRef.current) return;

      const { bottom, top } = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - bottom; // space under element
      if (spaceBelow < top) {
        const correctHeight = Math.max(110, top - 100);
        setMaxHeight(correctHeight);
        setMenuPlacement('top');
      } else {
        const correctHeight = Math.max(110, spaceBelow - 100);
        setMaxHeight(correctHeight);
        setMenuPlacement('bottom');
      }
    }

    updateMaxHeight();

    // Recalculate on window resize or scroll
    window.addEventListener('resize', updateMaxHeight);
    window.addEventListener('scroll', updateMaxHeight, true);

    return () => {
      window.removeEventListener('resize', updateMaxHeight);
      window.removeEventListener('scroll', updateMaxHeight, true);
    };
  }, [triggerRef]);

  const defaultPickerList =
    attendingAndHospicePhysicianList &&
    Array.isArray(attendingAndHospicePhysicianList)
      ? attendingAndHospicePhysicianList.concat([
          {
            value: 'more',
            label: 'more...',
          },
        ])
      : [];

  const isDisabled =
    readonly || medication.isprescription || (isMobile && !isMobileEditable);

  let physListWithoutOrdering;
  if (medication.orderingphysicianfirstname) {
    physListWithoutOrdering = defaultPickerList.filter(
      (phys) => phys.value !== medication.orderingphysicianid,
    );
  }
  const [docList, setDocList] = useState(
    physListWithoutOrdering || defaultPickerList,
  );

  const uneditable = isFromMedOrder && !!medication?.orderingphysicianfirstname;

  const onPhysicianChange = useCallback(
    (e) => {
      if (e.value === 'more') {
        setDocList(allDoctors);
        // manually re-open after data update
        setTimeout(() => selectRef.current?.select?.openMenu('first'), 0);
      } else {
        const orderingphysicianlastname = e.lastName;
        const orderingphysicianfirstname = e.firstName;
        const orderingphysiciancredentials = e.credentials;
        handleChange(medication, 'orderingphysician', {
          orderingphysicianid: e.value,
          orderingphysicianfirstname,
          orderingphysicianlastname,
          orderingphysiciancredentials,
        });
      }
    },
    [medication, handleChange],
  );
  /**
    The z-index property is only effective within a stacking context. 
    A new stacking context is created by elements with certain properties, including:
    position with a value other than static (like relative, absolute, fixed, or sticky) and a z-index value other than auto.
    opacity less than 1.
    transform or filter properties that are not none.
    Even if you set a high z-index (like 1000), your dropdown can still appear behind another element if it’s inside a different stacking context.   
    
    A new stacking context is created whenever an element has certain CSS properties.
    For example, any of these on a parent of your dropdown will create one:

    position: relative;  or absolute/fixed/sticky
    z-index: some_value;
    opacity: < 1;
    transform: scale(), translate(), etc.
    filter: blur(), brightness(), etc.
    mix-blend-mode
    will-change

    Option 1: Move the dropdown out of its stacking context.

    function Dropdown({ children }) {
      return createPortal(
        <div className="dropdown">{children}</div>,
        document.body
      );
    }
    Now the dropdown isn’t constrained by parent stacking contexts.
        
    */
  return (
    <StyledTableCell ref={triggerRef}>
      {uneditable ? (
        <div>{orderingPhysicianLabel(medication)}</div>
      ) : (
        <SuggestBox
          ref={selectRef}
          inputId={`physician-${medication.id}`}
          hasError={!medication.orderingphysicianfirstname}
          fitcontent
          placeholder={
            medication.orderingphysicianfirstname ? (
              orderingPhysicianLabel(medication)
            ) : (
              <FormattedMessage {...messages.choosePhys} />
            )
          }
          options={docList}
          disabled={isDisabled}
          onChange={onPhysicianChange}
          menuPortalTarget={document.body}
          maxMenuHeight={maxHeight}
          noMaxHeight
          menuPlacement={menuPlacement}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
      )}
    </StyledTableCell>
  );
};
